import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/appApi";
import { toast } from "sonner";
import { Flag } from "lucide-react";

const REASONS = ["Nudity to a minor / illegal content", "Harassment or abuse", "Spam or advertising", "Nudity where not wanted", "Other"];

export default function ReportDialog({ open, onOpenChange, reportedUserId, roomId, remoteStream }) {
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const captureFrame = async () => {
    try {
      if (!remoteStream || remoteStream.getVideoTracks().length === 0) return null;
      const video = document.createElement("video");
      video.srcObject = remoteStream;
      video.muted = true;
      video.playsInline = true;
      await video.play().catch(() => {});
      await new Promise((r) => setTimeout(r, 250));
      const w = video.videoWidth || 640;
      const h = video.videoHeight || 480;
      const scale = Math.min(1, 640 / w);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      video.pause();
      video.srcObject = null;
      return canvas.toDataURL("image/jpeg", 0.6);
    } catch (e) {
      return null;
    }
  };

  const submit = async () => {
    if (!reportedUserId) {
      toast.error("No active partner to report.");
      return;
    }
    setSubmitting(true);
    try {
      const screenshot = await captureFrame();
      await api.post("/reports", {
        reported_user_id: reportedUserId,
        reason: `${reason}${details ? " — " + details : ""}`,
        room_id: roomId,
        screenshot,
      });
      toast.success("Report submitted. Our moderators will review it.");
      onOpenChange(false);
      setDetails("");
    } catch (e) {
      toast.error("Could not submit report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Flag className="w-5 h-5 text-destructive" /> Report this user
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-sm">Reason</Label>
          <div className="grid gap-2">
            {REASONS.map((r) => (
              <button key={r} onClick={() => setReason(r)}
                data-testid={`report-reason-${r.slice(0, 6)}`}
                className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${reason === r ? "border-primary bg-primary/10" : "border-border bg-secondary hover:bg-accent"}`}>
                {r}
              </button>
            ))}
          </div>
          <Textarea placeholder="Add details (optional)" value={details} onChange={(e) => setDetails(e.target.value)}
            className="bg-secondary border-border" data-testid="report-details-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="report-submit-btn"
            className="bg-destructive hover:bg-destructive/90 text-white">
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
